/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { Logger, MessageService, ModalService } from '@core/lib/services';
import { AppConfigService } from '@app/common/app-config.service';
import { ContextService } from '@app/di/context.service';
import { UtilService } from '@app/util/util.service';
import { ExamCacheService } from '@app/ui/client-cache';
import { WorkItemService } from '@app/data-model/item/work-item.service';
import { ToggleKnobService } from '@app/ui/common/toggle-knob-service';
import {
  isVirtualGridGroup,
  VirtualGridGroup,
  VirtualGridNotification,
  VirtualGridNotificationAction
} from '@wfm/virtual-grid';
import { SearchList } from '@app/data-model/list/search-list';
import { ToDoList, ToDoListId } from '@app/data-model/list/to-do-list';
import { SearchService } from '@app/ui/search/search.service';
import { ToDoListService } from '@app/ui/common/work-list-navigator/to-do-list.service';
import { DatabaseListService } from '@app/ui/common/work-list-navigator/database-list.service';
import { EventMessage, Events, EventService } from '@app/common/event';
import { WorkListErrorCode } from '@app/common/types/work-list-error-code';
import { TranslateService } from '@ngx-translate/core';
import { SseSearchService } from '@app/ui/client-cache/sse-search.service';
import { RenderingPerformanceLog } from '@wfm/core';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { PatientNamePipe } from '@app/common/pipes';
import { AdvanceSearchService } from '@app/common/services/advance-search/advance-search.service';
import { Observable } from 'rxjs';
import { SaveWorkListComponent } from '@app/common/modals/components/save-work-list/save-work-list.component';
import { SearchCriteriaType, SearchParams } from '@app/common/types/search-params';

const log = new Logger('ListContainerService');

@Injectable({
  providedIn: 'root'
})
export class ListContainerService {
  private listItems: any[]; // Array of work-items
  private displayItems: any[]; // Array of work-items might be grouped by patient name
  private studyClickTime;
  private processedInitialPatient = false;
  private initialPatientInfo = [];
  private groupByCurrentContextPatientId = null;
  private reloadInitialPatient = false;
  private autoAdvanceIndex: number = undefined;
  private checkAssignedExamsAvailable: boolean;
  private lastActiveWorklist;
  private readonly groupByPatientPreference: boolean;
  private forceRefresh = false;
  private nextAssignedListItem;
  private autoAdvanceOnActiveWorklist = true;
  private openedExamIds = [];
  private skipIntegrationStatusCheck = true;
  private isWorklistChanged = false;
  private previousTarget;
  private assignmentCount = 0;
  private assignedWorkItems;
  private launchingNewExam = true;
  private isAutoAdvanceActive = false;
  private priorityChangeToStat = false;
  private deletedWorkListId: string;
  private fetchingNextPage = false;
  private errorState: VirtualGridNotification;
  private theEnd = false;
  private readonly workListPageSize: number;
  private listOfItems: any[];
  private activeListItem: { index: number; item: any; groupIndex: number } = {
    index: -1,
    item: undefined,
    groupIndex: -1
  };
  private artificiallyAddedItem: { list: any; item: any } = { list: -1, item: undefined };

  // Parameter used for idenfying the latest request.
  private lastFetchedCriteria: any;
  private lastStartIndex: any;
  private isActualSearch = true;

  constructor(
    private userPreferenceService: UserPreferenceService,
    private appConfigService: AppConfigService,
    private contextService: ContextService,
    private messageService: MessageService,
    private examCacheService: ExamCacheService,
    private workItemService: WorkItemService,
    private toggleKnobService: ToggleKnobService,
    private utilService: UtilService,
    private searchService: SearchService,
    private sseSearchService: SseSearchService,
    private toDoListService: ToDoListService,
    private eventService: EventService,
    private databaseListService: DatabaseListService,
    private translate: TranslateService,
    private advanceSearchService: AdvanceSearchService,
    private modalService: ModalService
  ) {
    this.registerEvents();
    this.groupByPatientPreference = this.userPreferenceService.getUserPreferenceFromCache('groupByPatient');
    this.workListPageSize = this.appConfigService.getWorklistPageSize();
  }

  openSaveAsWorkListModal(isLinkedList, data?: any): Observable<any> {
    return this.modalService.openModal(SaveWorkListComponent, {
      headerContent: isLinkedList
        ? this.translate.instant('searchModule.saveLinkedWorklistModalTitle')
        : this.translate.instant('searchModule.saveCustomWorklistModalTitle'),
      data: {
        searchParams: data?.searchParams ? data.searchParams : this.advanceSearchService.advanceSearchParams,
        searchParamsError: data?.searchParamsError
          ? data.searchParamsError
          : this.advanceSearchService.getSearchParamErrors(),
        isLinkedList
      },
      modalClass: 'modal--medium'
    }).afterClosed;
  }

  openSaveAsLinkedWorkListModal(searchParams: SearchParams): Observable<any> {
    this.advanceSearchService.advanceSearchParams = searchParams;
    return this.openSaveAsWorkListModal(true);
  }

  @RenderingPerformanceLog(false, undefined, function () {
    const list = this.appConfigService.getActiveList();
    return JSON.stringify({
      id: list.id,
      type: list.type,
      search: list.searchCriteriaInfo,
      filter: list.filterData,
      sort: list.sortData
    });
  })
  getNextPageItems(force: boolean, isAutoAdvance = false) {
    this.isActualSearch = false;
    if (force) {
      this.theEnd = false;
    }
    if (!this.isEndOfWorkList()) {
      return new Promise((resolve, reject) => {
        this.fetchingNextPage = true;
        const list = this.appConfigService.getActiveList();
        const startIndex = force ? 1 : this.listItems.length + 1;
        this.lastFetchedCriteria = list;
        this.lastStartIndex = startIndex;
        let dataFetchPromise: Promise<any>;
        if (list instanceof SearchList) {
          if (list.searchCriteriaInfo.isRemoteSearch()) {
            this.sseSearchService.populateNextPage(list);
            resolve(undefined);
          } else {
            dataFetchPromise = this.searchService.populateNextPage(list);
          }
        } else if (list instanceof ToDoList) {
          dataFetchPromise = this.toDoListService.populateNextPage(list, startIndex);
        } else if (list.isGlobal) {
          const params: SearchParams = new SearchParams(SearchCriteriaType.PATIENT_NAME);
          params.isGlobalWorklist = String(true);
          params.searchTimeStamp = new Date().getTime();
          list['searchCriteriaInfo'] = params;
          this.sseSearchService.populateNextPage(list);
          resolve(undefined);
        } else {
          dataFetchPromise = this.databaseListService.populateNextPage(list, startIndex);
        }
        dataFetchPromise &&
          dataFetchPromise
            .then((workItems: any[]) => {
              // Proceed with only the latest request. Old request will be ignored.
              // This logic need to be revisited once promise cancellation capability is available.
              if (_.isEqual(this.lastFetchedCriteria, list) && _.isEqual(this.lastStartIndex, startIndex)) {
                if (isAutoAdvance) {
                  this.resolvePageItems(list, workItems, force);
                  this.isActualSearch = true;
                  this.getIconDetailsForWorkItems(list).then(data => {
                    if (this.isActualSearch) {
                      if (data && data.updates instanceof Promise) {
                        resolve(undefined);
                      } else {
                        reject();
                      }
                    }
                  });
                } else {
                  resolve(this.resolvePageItems(list, workItems, force));
                  this.isActualSearch = true;
                  this.getIconDetailsForWorkItems(list);
                }
                this.messageService.broadcast('worklistLoaded.done');
                this.isActualSearch = true;
              }
            })
            .catch(ex => {
              if (_.isEqual(this.lastFetchedCriteria, list) && _.isEqual(this.lastStartIndex, startIndex)) {
                this.isActualSearch = true;
                this.theEnd = true;
                reject(ex);
              }
            });
      });
    }
  }

  getIconDetailsForWorkItems(list: any): Promise<any> {
    return this.workItemService
      .getIconDetailsForWorkItems(this.getListItems(), list)
      .then((data: { list: any; updates: Promise<any> }) => {
        data &&
          data.updates instanceof Promise &&
          data.updates
            .then(updates => {
              if (this.isActualSearch) {
                updates.protocolWorklist = list.protocolWorklist;
                this.workItemService.updateFromMap(this.getListItems(), updates);
                this.messageService.broadcast('applyVirtualGridChangeDetection', list);
              }
            })
            .catch(error => {
              log.error('There was a problem retrieving Work Item States and Indicators', error);
            });
      });
  }

  doAutoAdvance(lifecycle, targets) {
    const list = this.appConfigService.getActiveList();
    log.debug('auto advance will happen for worklist: ' + (list && list.id));
    const autoAdvancePreference = this.userPreferenceService.getUserPreferenceFromCache('autoadvance');
    if (autoAdvancePreference === 'none') {
      lifecycle.done();
      return;
    }
    this.studyClickTime = new Date().getTime();
    const target = targets;
    this.previousTarget = target;
    // This code block ensures that auto advance is happening only after READ_CLOSE for primary exam.
    if (this.contextService.getCurrentContext() !== null) {
      // The close action for an associated exam triggered auto advance. Auto advance is skipped until primary
      // exam closed.
      if (this.contextService.getCurrentContext() !== target.id) {
        log.info(
          'doAutoAdvance - READ_CLOSE event received for non-current exam. Current context: ' +
            this.contextService.getCurrentContext()
        );
        log.info('Target: ' + target.id + '- returning from doAutoAdvance');
        lifecycle.done();
        return;
      }
    }

    const tab = this.utilService.getTab(target.id);
    const goToNext = !(
      tab &&
      (tab.isExternalTab ||
        tab.isLaunchedFromPtHistory ||
        (tab.workItem &&
          tab.workItem.keys &&
          (tab.workItem.keys.setContextByGenEx || tab.workItem.keys.isOpenedFromUrl)))
    );
    log.debug('list-container -- Clear or Close action received, attempting to auto advance');
    this.messageService.broadcast('closeDxHubTabOnAutoNext', target);
    this.reloadInitialPatient = false;
    this.forceRefresh = false;
    this.checkAssignedExamsAvailable = true;
    if (this.processedInitialPatient) {
      this.initialPatientInfo = [];
      this.setInitialLaunchPatientInfo(target.id);
    }

    if (goToNext && this.isWorkListEligibleForAutoAdvance(list)) {
      this.examCacheService.removeWorkItemInfo(target.id);
      this.autoLaunchNextExam(target.id);
    }
    lifecycle.done();
  }

  private isWorkListEligibleForAutoAdvance(list) {
    return !(
      (list && list.id === ToDoListId.RECENTLY_VIEWED) ||
      list instanceof SearchList ||
      (list && list.isSearchList)
    );
  }

  private findNextItemID(id, listItems: any[]) {
    if (listItems && listItems.length) {
      let isNextItemValid = false;
      for (let index = 0; index < listItems.length; index++) {
        const item = listItems[index];
        if (
          isNextItemValid &&
          this.examCacheService.getAssociateExamCache(item.id) !== true &&
          item.state.extAppWorkItemState !== 'External_Read_Lock_Added' &&
          item.state.extAppWorkItemState !== 'RA1000_Approver_Added'
        ) {
          return item.id;
        }
        if (item.id === id) {
          isNextItemValid = true;
        }
      }
    }
  }

  activateWorkItem(listItem, doIntegrationStatusCheck?) {
    log.debug('list-container -- in $scope.activateWorkitem' + listItem.id);
    const activeWorkList = this.appConfigService.getActiveList();
    this.examCacheService.setWorkItemInfo(listItem.id, activeWorkList.displayName);
    this.groupByCurrentContextPatientId = listItem.patientId;

    this.workItemService
      .activate(listItem, {
        studyClickTime: this.studyClickTime,
        isAutoAdvance: doIntegrationStatusCheck
      })
      .then(() => {
        this.toggleKnobService.openDiagnosticHub();
      })
      .catch(ex => {
        log.error(
          `Auto advance - error occurred while launching next eligible exam, id - ${
            listItem.id
          }, Exception - ${JSON.stringify(ex)}`
        );
      });
  }

  isExamInWorkListForAutoAdvance(id): boolean {
    for (let i = 0; i < this.listOfItems.length; i++) {
      if (this.listOfItems[i].id === id) {
        return true;
      }
    }
    return false;
  }

  findListItemIndexById(id): any {
    for (let i = 0; i < this.listItems.length; i++) {
      if (this.listItems[i].id === id) {
        return i;
      }
    }
    return -1;
  }

  findGroupIndexById(patientId) {
    if (this.displayItems && this.displayItems.length && isVirtualGridGroup(this.displayItems[0])) {
      for (let i = 0; i < this.displayItems.length; i++) {
        if (this.displayItems[i].data.patientId === patientId) {
          return i;
        }
      }
    }
    return -1;
  }

  findListItemByProperty(property: string, value: any) {
    if (this.displayItems && this.displayItems.length && property && value) {
      if (isVirtualGridGroup(this.displayItems[0])) {
        for (let i = 0; i < this.displayItems.length; i++) {
          const group = this.displayItems[i];
          const found = group.items.find(obj => {
            return obj[property] === value;
          });
          if (found) {
            return found;
          }
        }
      } else {
        return this.displayItems.find(obj => {
          return obj[property] === value;
        });
      }
    }
  }

  getWorkItemIds(): any[] {
    return this.listItems && this.listItems.length && this.listItems.map(item => item.id);
  }

  applyUpdatesToItems(updates: any) {
    let count = 0;
    this.listItems.forEach(item => {
      const update = updates[item.id];
      if (update && this.workItemService.applyUpdateToItem(update, item)) {
        count++;
      }
    });
    return count;
  }

  findListItemById(id): any {
    if (this.displayItems && this.displayItems.length) {
      if (isVirtualGridGroup(this.displayItems[0])) {
        for (let i = 0; i < this.displayItems.length; i++) {
          const group = this.displayItems[i];
          const found = group.items.find(obj => {
            return obj.id === id;
          });
          if (found) {
            return found;
          }
        }
      } else {
        return this.displayItems.find(obj => {
          return obj.id === id;
        });
      }
    }
  }

  setCurrentContextPatientInfo() {
    const groupByPatientPreference = this.userPreferenceService.getUserPreferenceFromCache('groupByPatient');
    this.processedInitialPatient = false;
    if (groupByPatientPreference && this.isAutoAdvanceActive && this.groupByCurrentContextPatientId !== null) {
      let currentPatientGroupIndex;

      let grpByCurrentContextPtId = this.groupByCurrentContextPatientId;
      const initialPatientInfo = this.initialPatientInfo.length && this.initialPatientInfo[0];
      if (this.reloadInitialPatient && initialPatientInfo !== undefined) {
        currentPatientGroupIndex = initialPatientInfo.groupIndex;
        grpByCurrentContextPtId = initialPatientInfo.patientId;
        this.processedInitialPatient = true;
      } else {
        currentPatientGroupIndex = this.getPatientGroupingIndex(grpByCurrentContextPtId);
      }

      this.appConfigService.setGroupByPatientContext({
        groupByCurrentContextPatientId: grpByCurrentContextPtId,
        currentPatientGroupingIndex: currentPatientGroupIndex
      });
    }
  }

  clearListItems() {
    this.listItems = [];
    this.displayItems = [];
    this.errorState = undefined;
    return this.displayItems;
  }

  getListItems() {
    return this.listItems;
  }

  getDisplayItems() {
    return this.displayItems;
  }

  setIsFetchingNextPage(val: boolean) {
    this.fetchingNextPage = val;
  }

  getIsFetchingNextPage() {
    return this.fetchingNextPage;
  }

  setLaunchingNewExam(val: boolean) {
    this.launchingNewExam = val;
  }

  setLastActiveWorkList(val) {
    this.lastActiveWorklist = val;
  }

  setCheckAssignedExamsAvailable(val) {
    this.checkAssignedExamsAvailable = val;
  }

  setAutoAdvanceIndexes(val: number) {
    this.autoAdvanceIndex = val;
  }

  setAutoAdvanceActive(val: boolean) {
    this.isAutoAdvanceActive = val;
  }

  getAutoAdvanceActive() {
    return this.isAutoAdvanceActive;
  }

  setPriorityChangeToStat(val: boolean) {
    this.priorityChangeToStat = val;
  }

  getPriorityChangeToStat() {
    return this.priorityChangeToStat;
  }

  setDeletedWorkListId(val: string) {
    this.deletedWorkListId = val;
  }

  getDeletedWorkListId() {
    return this.deletedWorkListId;
  }

  getListErrorNotification(errorCode: number, config?: any) {
    let notification: VirtualGridNotification;
    switch (errorCode) {
      case WorkListErrorCode.USER_DEFAULT_WL: {
        notification = this.getNotification(
          this.translate.instant('notification.defaultWL.primary'),
          this.translate.instant('notification.defaultWL.secondary'),
          {
            id: 'default-list',
            label: this.translate.instant('notification.defaultWL.actionText')
          }
        );
        break;
      }
      case WorkListErrorCode.SEARCH_TIMEOUT: {
        notification = this.getNotification(
          this.translate.instant('workloadManager.listContainer.search.error.timeout')
        );
        break;
      }
      case WorkListErrorCode.TIMEOUT: {
        notification = this.getNotification(this.translate.instant('workloadManager.listContainer.error.timeout'));
        break;
      }
      case WorkListErrorCode.SEARCH_NO_RESULTS: {
        if (config.isAdvancedSearchList) {
          notification = this.getNotification(
            this.translate.instant('advanced.Search.noResults'),
            this.translate.instant('notification.workList.search.secondary')
          );
        } else {
          const label = this.appConfigService.isNHIEnabled()
            ? this.utilService.getLocalizedNHISpecificText(
                config.searchCriteriaLabel,
                this.appConfigService.getNHIType()
              )
            : this.translate.instant(config.searchCriteriaLabel);
          notification = this.getNotification(
            `${this.translate.instant('notification.workList.search.primary')}
            ${label}: ${config.searchCriteria}`,
            this.translate.instant('notification.workList.search.secondary')
          );
        }
        break;
      }
      case WorkListErrorCode.NO_RESULTS: {
        notification = this.getNotification(this.translate.instant('notification.workList.noResults.primary'));
        break;
      }
      case WorkListErrorCode.FILTER_NO_RESULTS: {
        notification = this.getNotification(
          this.translate.instant('notification.workList.filter.primary'),
          this.translate.instant('notification.workList.filter.secondary')
        );
        break;
      }
      case WorkListErrorCode.WORK_LIST_DELETED: {
        notification = this.getNotification(
          this.translate.instant('workloadManager.listContainer.error.CAToolWorklistDeletion')
        );
        break;
      }
      case WorkListErrorCode.XE_DOWN: {
        notification = this.getNotification(this.translate.instant('xeServiceDown.examsMsg'));
        break;
      }
      default: {
        notification = this.getNotification(this.translate.instant('workloadManager.listContainer.error.timeout'));
        break;
      }
    }
    this.errorState = notification;
    return notification;
  }

  private getNotification(
    primary: string,
    secondary: string = undefined,
    action: VirtualGridNotificationAction = undefined
  ): VirtualGridNotification {
    return {
      primaryText: primary,
      secondaryText: secondary,
      notificationAction: action
    };
  }

  private isNextAutoLaunchedExam(workItem) {
    return (
      workItem.state &&
      !workItem.state.isAssignedToOther &&
      !workItem.state.isActivatedByOther &&
      this.examCacheService.getAssociateExamCache(workItem.id) !== true &&
      this.examCacheService.getSkipAutoAdvanceExamCache(workItem.id) !== true
    );
  }

  private iterateItemsInPage(items: any[]) {
    if (items && items.length) {
      for (let index = this.autoAdvanceIndex || 0; index < items.length; index++) {
        const listItem = items[index];
        if (this.isNextAutoLaunchedExam(listItem) && this.currentWorkItemId !== listItem.id) {
          const tab = this.utilService.getTab(listItem.id);
          if (tab && tab.isExternalTab) {
            continue;
          }
          log.debug('list-container -- Launching next exam --- found next work item');
          this.autoAdvanceIndex = index;
          return listItem;
        }

        if (listItem.id === this.currentWorkItemId) {
          log.debug('list-container -- Launching next exam --- found current work item');
          this.autoAdvanceIndex = index;
        }
      }
    }
  }

  private currentWorkItemId = undefined;
  private autoLaunchNextExam(currentWorkItemId, callFromAssignedList?) {
    this.examCacheService.removeWorkItemInfo(currentWorkItemId);
    const autoAdvancePreference = this.userPreferenceService.getUserPreferenceFromCache('autoadvance');
    if (
      autoAdvancePreference === 'none' ||
      (this.errorState !== undefined && this.lastActiveWorklist === this.deletedWorkListId)
    ) {
      this.isAutoAdvanceActive = false;
      return;
    } else if (autoAdvancePreference === 'activeWorklist') {
      this.autoAdvanceOnActiveWorklist = true;
    }

    this.currentWorkItemId = currentWorkItemId;

    if (
      (autoAdvancePreference === 'activeWorklistAndAssigned' || autoAdvancePreference === 'assigned') &&
      this.checkAssignedExamsAvailable === true
    ) {
      this.autoLaunchNextAssignedExam();
      return;
    }

    this.findAutoAdvanceIndex(currentWorkItemId);
    if (this.autoAdvanceIndex > -1) {
      if (this.autoAdvanceOnActiveWorklist === true && autoAdvancePreference !== 'assigned') {
        log.debug('callFromAssignedList: ' + callFromAssignedList);
        let listItem;
        let skipActivateItem = false;

        if (callFromAssignedList) {
          if (this.groupByPatientPreference) {
            if (!this.forceRefresh) {
              listItem = this.nextEligibleItem();
            }
            if (listItem === undefined || this.forceRefresh) {
              this.setNextPatientGroupingItem();
              skipActivateItem = true;
            }
          } else {
            listItem = this.nextEligibleItem();
          }
        } else {
          listItem = this.iterateItemsInPage(this.listItems);
        }
        if (listItem && !skipActivateItem) {
          this.isAutoAdvanceActive = true;
          this.activateWorkItem(listItem, this.skipIntegrationStatusCheck);
          return;
        }

        this.findItemInNextPage();
      }
    }
  }

  private findAutoAdvanceIndex(currentWorkItemId) {
    if (this.priorityChangeToStat) {
      this.autoAdvanceIndex = 0;
      this.priorityChangeToStat = false;
    } else {
      this.autoAdvanceIndex = this.findListItemIndexById(currentWorkItemId);
    }
  }

  private findItemInNextPage() {
    if (!this.groupByPatientPreference) {
      //we still haven't found the listItem! Load more pages
      const pagePromise = this.getNextPageItems(false, true);
      if (pagePromise) {
        pagePromise.then(() => {
          this.afterFetchPage(this.listItems);
        });
      } else {
        this.isAutoAdvanceActive = false;
      }
    }
  }

  private launchAssignedExams() {
    log.debug('Assignment items count: ' + this.assignmentCount);
    const autoAdvancePreference = this.userPreferenceService.getUserPreferenceFromCache('autoadvance');
    if (this.assignmentCount === 0) {
      this.checkAssignedExamsAvailable = false;
      if (this.isAutoAdvanceStopping(autoAdvancePreference)) {
        this.eventService.sendEvent(new EventMessage(Events.closediscard, { workItem: this.previousTarget }));
      } else {
        this.autoLaunchNonAssignedExam();
      }
    } else {
      this.checkAssignedExamsAvailable = true;
      this.autoAdvanceIndex = undefined;
      this.removeItemFromAssignedList(this.currentWorkItemId);
      this.workItemService
        .getWorkItemStates(this.assignedWorkItems)
        .then(() => {
          this.nextAssignedListItem = this.iterateItemsInPage(this.assignedWorkItems);
          if (this.nextAssignedListItem) {
            log.debug(
              'Launch next Exam from assigned exams id ::' +
                this.nextAssignedListItem.id +
                ' Accession:' +
                this.nextAssignedListItem.accession
            );
            this.activateWorkItem(this.nextAssignedListItem, this.skipIntegrationStatusCheck);
            this.isAutoAdvanceActive = true;
            this.removeItemFromAssignedList(this.nextAssignedListItem.id);
            this.assignmentCount--;
            return;
          } else if (this.nextAssignedListItem === undefined && autoAdvancePreference === 'activeWorklistAndAssigned') {
            this.autoAdvanceOnActiveWorklist = true;
            this.checkAssignedExamsAvailable = false;
            this.launchItemFromTemporaryList();
          } else {
            this.assignmentCount = 0;
          }
        })
        .catch(ex => {
          log.error('Auto advance failed - unable find the assigned exam', JSON.stringify(ex));
        });
    }
  }

  private autoLaunchNonAssignedExam() {
    // if current active exam does not belong to the the active worklist, stop auto advance
    if (!this.isExamInWorkListForAutoAdvance(this.currentWorkItemId)) {
      log.debug('list-container -- in autoLaunchNonAssignedExam; stopping auto advance');
      this.isAutoAdvanceActive = false;
      return;
    }

    this.autoAdvanceOnActiveWorklist = true;
    this.checkAssignedExamsAvailable = false;
    this.reloadInitialPatient = true;
    this.autoAdvanceIndex = undefined;
    log.debug('In autoLaunchNonAssignedExam, currentWorkItemId: ' + this.currentWorkItemId);
    this.skipLastAssignedExam(this.currentWorkItemId);
    const initialExamId = this.openedExamIds[0];
    log.debug('In autoLaunchNonAssignedExam, intitalExamId: ' + initialExamId);
    const nextEligibleExamId = this.checkForNextItemFromActiveWorklist(initialExamId);
    if (nextEligibleExamId) {
      this.openedExamIds = [];
      log.debug('list-container -- Next Item id from the list of items ' + nextEligibleExamId);
      this.autoLaunchNextExam(nextEligibleExamId, true);
      return;
    } else {
      if (this.priorityChangeToStat) {
        this.autoLaunchNextExam(initialExamId, false);
        return;
      }

      log.debug('Exam moved out of worklist, finding in temporary list ' + nextEligibleExamId);
      this.launchItemFromTemporaryList();
    }
  }

  private autoLaunchNextAssignedExam() {
    this.openedExamIds.push(this.currentWorkItemId);
    this.setInitialLaunchPatientInfo(this.currentWorkItemId);
    const params = {} as any;
    params.filter = {};
    this.autoAdvanceOnActiveWorklist = false;
    params.pageNumber = 0;
    params.pageStart = 1;
    params.pageEnd = 500;
    params.filter.actorName = this.appConfigService.getUserId();
    params.filter.actionCode = 'ASSIGN';
    if (this.launchingNewExam === true || this.assignmentCount === 0) {
      log.debug('Service call to fetch assigned workItems');
      const assignedList = this.toDoListService.getToDoListById(ToDoListId.ASSIGNED);
      if (assignedList) {
        this.toDoListService
          .populateNextPage(assignedList)
          .then((workItems: any[]) => {
            if (workItems && workItems.length) {
              this.assignedWorkItems = workItems;
              this.removeItemFromAssignedList(this.currentWorkItemId);
              workItems.forEach(workItem => {
                if (
                  this.examCacheService.getSkipAutoAdvanceExamCache(workItem.id) === true &&
                  this.currentWorkItemId !== workItem.id
                ) {
                  this.removeItemFromAssignedList(workItem.id);
                }
              });
              this.assignmentCount = this.assignedWorkItems.length;
            } else {
              this.assignmentCount = 0;
            }
          })
          .catch(() => {
            this.assignedWorkItems = [];
            this.assignmentCount = 0;
          })
          .finally(() => {
            this.reloadInitialPatient = false;
            this.launchAssignedExams();
          });
      }
    } else {
      this.reloadInitialPatient = false;
      this.launchAssignedExams();
    }
  }

  private launchItemFromTemporaryList() {
    const initialExamId = this.openedExamIds[0];
    let listOfItems;
    log.debug('list-container -- in launchItemFromTemporaryList, initialExamId: ' + initialExamId);
    for (let index = 1; index < this.openedExamIds.length; index++) {
      listOfItems = _.without(this.listOfItems, _.findWhere(this.listOfItems, { id: this.openedExamIds[index] }));
    }
    log.debug('list of ids: ' + _.pluck(listOfItems, 'id'));
    this.openedExamIds = [];
    const nextEligibleExamId = this.findNextItemID(initialExamId, listOfItems);
    if (nextEligibleExamId) {
      this.autoLaunchNextEligibleExam(nextEligibleExamId);
    } else {
      this.currentWorkItemId = initialExamId;
      this.findItemInNextPage();
    }
  }

  private skipLastAssignedExam(currentExamId) {
    if (this.appConfigService.getAppConfigByKey('reportingConfig') === 'true') {
      this.examCacheService.setSkipAutoAdvanceExamCache(currentExamId);
    }
  }

  private checkForNextItemFromActiveWorklist(initialExamId) {
    return this.findNextItemID(initialExamId, this.listItems);
  }

  private autoLaunchNextEligibleExam(nextEligibleExamId) {
    if (nextEligibleExamId) {
      this.forceRefresh = true;
      this.autoLaunchNextExam(nextEligibleExamId, true);
      return;
    } else {
      this.isAutoAdvanceActive = false;
      log.debug('list-container-- Unable to find next item, stopping autoAdvance');
    }
  }

  private removeItemFromAssignedList(id) {
    this.assignedWorkItems = _.without(this.assignedWorkItems, _.findWhere(this.assignedWorkItems, { id: id }));
  }

  private isAutoAdvanceStopping(autoAdvancePreference): boolean {
    return (
      autoAdvancePreference === 'assigned' ||
      (autoAdvancePreference === 'activeWorklistAndAssigned' &&
        !this.isExamInWorkListForAutoAdvance(this.previousTarget.id)) ||
      this.appConfigService.getActiveList().id === 'assigned'
    );
  }

  private setNextPatientGroupingItem() {
    return new Promise((resolve, reject) => {
      this.setCurrentContextPatientInfo();
      const promise = this.getNextPageItems(true, true);
      promise &&
        promise.then(
          () => {
            this.activateWorkItemForGroupByPatient();
            resolve(true);
          },
          () => {
            this.endLoadingCycleHelper();
            reject();
          }
        );
    });
  }

  private activateWorkItemForGroupByPatient() {
    let listItem = this.nextEligiblePatientGroupingItem();
    if (!listItem) {
      listItem = this.nextEligibleItem();
    }
    if (listItem) {
      this.activateWorkItem(listItem, this.skipIntegrationStatusCheck);
    }
  }

  private nextEligiblePatientGroupingItem() {
    const initialPatientId = this.initialPatientInfo[0].patientId;

    for (let index = this.autoAdvanceIndex || 0; index < this.listItems.length; index++) {
      const listItem = this.listItems[index];
      if (listItem.patientId === initialPatientId) {
        this.autoAdvanceIndex = index;
      }

      if (this.isNextAutoLaunchedExam(listItem)) {
        const tab = this.utilService.getTab(listItem.id);
        if (tab && tab.isExternalTab) {
          continue;
        }
        this.autoAdvanceIndex = index;
        return listItem;
      }
    }
    this.autoAdvanceIndex = this.autoAdvanceIndex === undefined ? undefined : 0;
  }

  private endLoadingCycleHelper() {
    this.fetchingNextPage = false;
    this.messageService.broadcast('globalLoading.stop');
    this.appConfigService.setGroupByPatientContext(null);
    if (this.isWorklistChanged) {
      this.isWorklistChanged = false;
    }
  }

  private nextEligibleItem() {
    for (let i = this.autoAdvanceIndex; i < this.listItems.length; i++) {
      const listItem = this.listItems[i];

      if (listItem.id === this.currentWorkItemId) {
        log.debug('list-container -- Launching next exam -- found current work item');
        this.autoAdvanceIndex = i;
      }

      if (this.isNextAutoLaunchedExam(listItem)) {
        const tab = this.utilService.getTab(listItem.id);
        if (tab && tab.isExternalTab) {
          continue;
        }
        log.debug('list-container -- Launching next exam -- found next work item');
        this.autoAdvanceIndex = i;
        return listItem;
      }
    }
  }

  private afterFetchPage(items: any[]) {
    if (!items || (items && !items.length)) {
      //we didn't get a page back
      this.isAutoAdvanceActive = false;
      return;
    }
    const listItem = this.iterateItemsInPage(items);
    if (listItem) {
      //if we have a winning list item, this is the new index
      this.autoAdvanceIndex = this.findListItemIndexById(listItem.id);
      //no timeout because it's already been delayed by loading an additional page.
      this.activateWorkItem(listItem, this.skipIntegrationStatusCheck);
      this.isAutoAdvanceActive = true;
    } else {
      const pagePromise = this.getNextPageItems(false, true);
      if (pagePromise) {
        pagePromise.then(
          () => {
            this.afterFetchPage(this.listItems);
          },
          error => {
            this.isAutoAdvanceActive = false;
            log.error('auto advance fetching next page failed, ERROR: ', error);
          }
        );
      } else {
        this.isAutoAdvanceActive = false;
      }
    }
  }

  private getPatientIdFromList(examId) {
    const item = this.findListItemById(examId);
    if (item) {
      return item.patientId;
    }
    return 0;
  }

  private setInitialLaunchPatientInfo(examId) {
    const patientId = this.getPatientIdFromList(examId);
    if (patientId !== 0) {
      const patientItem = _.findWhere(this.initialPatientInfo, { patientId: patientId });
      const patientIdIndex = _.indexOf(this.initialPatientInfo, patientItem);
      if (patientIdIndex === -1) {
        const groupByPtIndexDetails = { patientId: patientId, groupIndex: this.getPatientGroupingIndex(patientId) };
        this.initialPatientInfo.push(groupByPtIndexDetails);
      }
    }
  }

  private getGroupedByItems() {
    const groupedDisplayItems: VirtualGridGroup[] = [];
    this.listItems &&
      this.listItems.length &&
      this.listItems.forEach((item: any) => {
        if (item.displayPatientNameHeader) {
          groupedDisplayItems.push({
            labels: [
              {
                data: PatientNamePipe.transform(item.patientName)
              }
            ],
            data: item,
            items: [item]
          });
        } else {
          groupedDisplayItems[groupedDisplayItems.length - 1].items.push(item);
        }
      });
    return groupedDisplayItems;
  }

  resolvePageItems(list, workItems: any[], force: boolean): any[] {
    let items: any[];
    if (workItems && workItems.length) {
      if (
        list.isSearchList ||
        this.userPreferenceService.getUserPreferenceFromCache('groupByPatient') ||
        workItems.length < this.workListPageSize
      ) {
        this.theEnd = true;
      }
      for (const item of workItems) {
        item.priority = UtilService.getLocalizedPriority(item.priority, this.translate);
      }
      if (force) {
        this.listItems = workItems;
        this.processActiveItemWhenRefreshWL();
      } else {
        this.listItems.push(...workItems);
      }
      this.setActiveListItem(-1);
      this.setDisplayItems(list.isAdvancedSearchList);
      items = this.displayItems;
    } else {
      if (force) {
        this.listItems = [];
        this.setDisplayItems(list.isAdvancedSearchList);
        items = this.displayItems;
      }
      this.theEnd = true;
    }

    return items;
  }

  private processActiveItemWhenRefreshWL() {
    if (this.activeListItem && this.activeListItem.index > -1) {
      const index = this.findListItemIndexById(this.activeListItem.item.id);
      if (index < 0) {
        const isGroupByPatient = this.userPreferenceService.getUserPreferenceFromCache('groupByPatient');
        if (isGroupByPatient) {
          const groupIndex = this.findGroupIndexById(this.activeListItem.item);
          if (groupIndex > -1) {
            (this.displayItems[groupIndex] as VirtualGridGroup).items.splice(
              this.activeListItem.index,
              0,
              this.activeListItem.item
            );
          } else {
            this.displayItems.splice(this.activeListItem.groupIndex, 0, {
              data: this.activeListItem.item,
              items: [this.activeListItem.item]
            } as VirtualGridGroup);
          }
        } else {
          // inserting at the previous index if not group by patient
          this.listItems.splice(this.activeListItem.index, 0, this.activeListItem.item);
        }
        this.artificiallyAddedItem = {
          list: this.appConfigService.getActiveList(),
          item: this.activeListItem.item
        };
      }
    }
  }

  removeArtificiallyAddedItem() {
    if (
      this.artificiallyAddedItem &&
      this.artificiallyAddedItem.item &&
      this.artificiallyAddedItem.list &&
      this.appConfigService.getActiveList().id === this.artificiallyAddedItem.list.id
    ) {
      const isGroupByPatient = this.userPreferenceService.getUserPreferenceFromCache('groupByPatient');
      if (isGroupByPatient) {
        const groupIndex = this.findGroupIndexById(this.artificiallyAddedItem.item.patientId);
        if (groupIndex > -1) {
          const items = (this.displayItems[groupIndex] as VirtualGridGroup).items;
          if (items.length > 1) {
            const index = _.findIndex(items, { id: this.artificiallyAddedItem.item.id });
            if (index > -1) {
              items.splice(index, 1);
            }
          } else {
            this.displayItems.splice(groupIndex, 1);
          }
        }
      } else {
        const index = this.findListItemIndexById(this.artificiallyAddedItem.item.id);
        if (index > -1) {
          this.listItems.splice(index, 1);
        }
      }
    }
    this.artificiallyAddedItem = {
      item: undefined,
      list: undefined
    };
  }

  private isWorklistItemsGrouped(isAdvSearchItems?: boolean) {
    const list = this.appConfigService.getActiveList();
    return (
      (!isAdvSearchItems && list instanceof SearchList) ||
      (this.userPreferenceService.getUserPreferenceFromCache('groupByPatient') &&
        !(list instanceof SearchList) &&
        list.id !== ToDoListId.RECENTLY_VIEWED)
    );
  }

  private setDisplayItems(isAdvSearchItems?: boolean) {
    if (this.isWorklistItemsGrouped(isAdvSearchItems)) {
      this.displayItems = this.getGroupedByItems();
    } else {
      this.displayItems = this.listItems;
    }
  }

  private getUniquePatientIDList() {
    return _.uniq(this.listItems, true, item => {
      return item.patientId;
    });
  }

  private getPatientGroupingIndex(patientId) {
    const uniquePatientIdList = this.getUniquePatientIDList();
    const patientItem = _.findWhere(uniquePatientIdList, { patientId: patientId });
    return _.indexOf(uniquePatientIdList, patientItem);
  }

  setActiveItemBeforeRefreshWorkList(activeTab): boolean {
    let index = -1;
    if (activeTab) {
      index = this.findListItemIndexById(activeTab.id);
    }
    this.setActiveListItem(index);
    return index > -1;
  }

  setActiveListItem(index) {
    const isGroupByPatient = this.userPreferenceService.getUserPreferenceFromCache('groupByPatient');
    if (isGroupByPatient) {
      const groupIndex = index > -1 ? this.findGroupIndexById(this.listItems[index].patientId) : -1;
      const item = index > -1 ? this.listItems[index] : undefined;
      this.activeListItem = {
        index:
          groupIndex > -1 && item
            ? _.findIndex((this.displayItems[groupIndex] as VirtualGridGroup).items, { patientId: item.patientId })
            : -1,
        groupIndex,
        item
      };
    } else {
      this.activeListItem = {
        index: index,
        groupIndex: -1,
        item: index > -1 ? this.listItems[index] : undefined
      };
    }
  }

  resetAssignmentFlag() {
    this.checkAssignedExamsAvailable = true;
    this.openedExamIds = [];
    this.initialPatientInfo = [];
    this.assignmentCount = 0;

    this.updateCopyOfListItemsForAutoAdvance();
  }

  setEndOfWorkList(flag: boolean) {
    this.theEnd = flag;
  }

  isEndOfWorkList() {
    return this.theEnd;
  }

  private updateCopyOfListItemsForAutoAdvance() {
    this.listOfItems = [...this.listItems];
  }

  private registerEvents() {
    _.each(['clear', 'close', 'closeviewer', 'autoAdvanceSkipAssign', 'autoAdvanceSkipUnAssign'], actionName => {
      this.eventService.beforeEvent(actionName, (lifecycle, targets) => {
        this.doAutoAdvance(lifecycle, targets);
      });
    });

    this.messageService.on('skipAutoAdvance').subscribe(id => {
      const tab = this.utilService.getActiveTab();
      if (tab && tab.id === id) {
        const workItem = tab && tab.workItem;
        let promise: Promise<any>;
        const eventWorkItem = new EventWorkItem(tab.id, workItem && workItem.keys);
        eventWorkItem.associationList = this.eventService.getAssociatedIds(tab.id);
        eventWorkItem.actionType = 'skipExam';
        if (eventWorkItem.associationList && eventWorkItem.associationList.length > 0) {
          this.examCacheService.setAssociateExamCache(tab.id);
          _.each(eventWorkItem.associationList, id => {
            this.examCacheService.setAssociateExamCache(id);
          });
        }
        if (this.examCacheService.getAssignedExamCache(id)) {
          promise = this.eventService.sendEvent(new EventMessage(Events.autoAdvanceSkipAssign, eventWorkItem));
        } else {
          promise = this.eventService.sendEvent(new EventMessage(Events.autoAdvanceSkipUnAssign, eventWorkItem));
        }
        promise &&
          promise.catch(ex => {
            log.error('Error occurred while skipping auto advance, Exception', JSON.stringify(ex));
          });
      }
    });
  }
}
